import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const OPENING_BG =
  "https://static.prod-images.emergentagent.com/jobs/62c4d2db-e67b-4f45-8693-2623831d62c9/images/07a90b21d530bca5949ec604d099ff28736c793b29e4d564fd829a1ad358a655.jpeg";

const ACCENT = "#4FA3D9";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.4 + i * 0.22 },
  }),
};

export const OpeningScreen = ({ onEnter, onHome }) => {
  return (
    <div
      className="relative h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] grain"
      data-testid="opening-screen"
    >
      {/* Background image with slow cinematic zoom */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0, scale: 1.12 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
      >
        <img
          src={OPENING_BG}
          alt=""
          className="h-full w-full object-cover animate-slow-zoom"
        />
      </motion.div>

      {/* Soft drifting light glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute left-1/2 top-1/3 h-[60vh] w-[60vh] -translate-x-1/2 rounded-full blur-[120px] animate-light-drift"
          style={{ background: "rgba(79,163,217,0.22)" }}
        />
      </div>

      {/* Darkening gradients for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/55 to-[#0a0a0a]/40" />
      <div className="absolute inset-0 bg-[#0a0a0a]/25" />

      {/* Top-left brand */}
      <motion.div
        className="absolute left-6 top-7 z-20 flex items-center gap-1 sm:left-8 sm:top-8"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
        data-testid="brand-logo"
      >
        <img src="/adaptience-logo.png" alt="Adaptience" onClick={onHome} role="button" tabIndex={0} aria-label="Adaptience home" className="h-6 w-auto cursor-pointer sm:h-7" data-testid="opening-logo" />
        <sup className="text-[9px] tracking-normal text-white/50">™</sup>
      </motion.div>

      {/* Bottom-left content block (movie-poster tension) */}
      <div className="absolute inset-x-0 bottom-0 z-20 px-6 pb-12 sm:px-10 sm:pb-16">
        <div className="max-w-xl">
          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={0}
            className="mb-5 text-xs font-medium uppercase tracking-[0.32em] text-white/50"
            data-testid="opening-overline"
          >
            An interactive life simulation
          </motion.p>

          <motion.h1
            variants={fade}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl"
            data-testid="opening-title"
          >
            They Don&rsquo;t
            <br />
            Want Me Here
          </motion.h1>

          <motion.p
            variants={fade}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-5 max-w-md text-sm leading-relaxed text-white/60 sm:text-base"
            data-testid="opening-subtitle"
          >
            A social exclusion simulation for ages 10&ndash;14.
          </motion.p>

          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-10"
          >
            <button
              onClick={onEnter}
              data-testid="enter-simulation-btn"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full border px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] transition-[color,background-color,box-shadow] duration-500 active:scale-[0.98]"
              style={{
                borderColor: ACCENT,
                color: ACCENT,
                boxShadow: `inset 0 0 24px rgba(79,163,217,0.14), 0 0 24px rgba(79,163,217,0.12)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = ACCENT;
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = ACCENT;
              }}
            >
              Begin
              <ArrowRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-1"
              />
            </button>

            <p
              className="mt-6 max-w-sm text-xs leading-relaxed text-white/40"
              data-testid="age-guidance-note"
            >
              Designed for ages 10&ndash;14. For younger users, we recommend experiencing this with a parent or guardian.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OpeningScreen;
